import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  Chip,
  Avatar,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  Tooltip,
  styled,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Alert,
  TablePagination
} from '@mui/material';
import {
  Close as CloseIcon,
  Groups as GroupsIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Flag as FlagIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarTodayIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  DoneAll as DoneAllIcon,
  Pending as PendingIcon,
  PlayCircleFilled as PlayCircleFilledIcon,
  Pause as PauseIcon,
  History as HistoryIcon,
  Add as AddIcon,
  ThumbUp as ThumbUpIcon,
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { taskDataService } from '../../../services/taskDataService';

// Stil tanımlamaları
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    minWidth: 900,
  },
}));

const StatusChip = styled(Chip)<{ customcolor?: string }>(({ theme, customcolor }) => ({
  fontWeight: 'bold',
  backgroundColor: customcolor ? `${customcolor}20` : undefined,
  color: customcolor,
  borderColor: customcolor ? `${customcolor}50` : undefined,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

const TaskMemberAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  fontSize: '0.875rem',
  marginRight: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
}));

const TabPanel = (props: { children?: React.ReactNode; value: number; index: number }) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`team-task-tabpanel-${index}`}
      aria-labelledby={`team-task-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Akış öğesi tipi tanımı
interface ActivityItem {
  id: string;
  taskId: string;
  taskName: string;
  teamMemberId: string;
  teamMemberName: string;
  action: string;
  timestamp: number;
  taskOrder?: number;
}

interface TeamTaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  projectSqlId?: number | null;
  companyId?: string;
  onChanged?: () => void;
}

const TeamTaskDetailModal: React.FC<TeamTaskDetailModalProps> = ({
  open,
  onClose,
  projectSqlId,
  companyId
}) => {
  const [project, setProject] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Akış sekmesi için state'ler
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(10);

  // Resim görüntüleme diyaloğu için state'ler
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Resim diyaloğunu açma işlevi
  const handleOpenImageDialog = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  // Resim diyaloğunu kapatma işlevi
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  // Sekme değişikliği işleyicisi
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Sekme değiştiğinde sayfalama sayaçlarını sıfırla
    if (newValue === 0) {
      setPage(0);
    } else if (newValue === 1) {
      setActivityPage(0);
    }
  };

  // Sayfa değişikliği
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır değişikliği
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Akış sekmesi için sayfalama işleyicileri
  const handleActivityChangePage = (event: unknown, newPage: number) => {
    setActivityPage(newPage);
  };

  const handleActivityChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActivityRowsPerPage(parseInt(event.target.value, 10));
    setActivityPage(0);
  };

  // SQL'den proje + görev + durum geçmişi (taskDataService)
  useEffect(() => {
    if (!open || !projectSqlId || !companyId) return;

    const toMs = (iso?: string | null): number => (iso ? new Date(iso).getTime() : 0);
    const parseImageUrls = (raw?: string | null): string[] => {
      if (!raw) return [];
      try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
    };

    const loadProjectData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const detail = await taskDataService.getTeamProject(projectSqlId);
        if (!detail) { setError('Proje bulunamadı'); return; }

        const { project: p, tasks: projectTasks } = detail;

        const projectInfo = {
          id: p.id,
          name: p.name || 'İsimsiz Proje',
          teamId: p.teamGroupId != null ? String(p.teamGroupId) : '',
          teamName: p.teamName || '',
          createdAt: toMs(p.createdAt),
          status: p.status || 'active',
          isSequential: p.isSequential || false,
          activeTaskOrder: p.activeTaskOrder,
          groupId: p.groupId,
          groupName: p.groupName || '',
          branchId: p.branchId,
          branchName: p.branchName || '',
          description: ''
        };

        const tasksArray = (projectTasks || []).map((tk) => ({
          id: tk.id,
          name: tk.name || 'İsimsiz Görev',
          description: tk.description || '',
          teamMemberId: tk.teamMemberUid || '',
          teamMemberName: tk.teamMemberName || 'Atanmamış',
          status: tk.status || 'pending',
          priority: tk.priority || 'medium',
          taskOrder: tk.taskOrder,
          createdAt: toMs(tk.createdAt),
          deadline: tk.dueDate ? toMs(tk.dueDate) : undefined,
          lastUpdatedAt: tk.lastUpdatedAt ? toMs(tk.lastUpdatedAt) : undefined,
          lastUpdatedByName: tk.lastUpdatedByName,
          imageUrls: parseImageUrls(tk.imageUrls),
        }));

        // Durum geçmişi (akış sekmesi) — görev başına ayrı geçmiş çağrısı
        const activitiesArray: ActivityItem[] = [];
        const histories = await Promise.all(
          tasksArray.map(t => taskDataService.getTeamTaskHistory(projectSqlId, t.id as number).catch(() => []))
        );
        const trackedActions = new Set(['accepted', 'started', 'paused', 'resumed', 'completed']);
        tasksArray.forEach((t, idx) => {
          (histories[idx] || []).forEach((h) => {
            if (!trackedActions.has(h.statusKey)) return;
            activitiesArray.push({
              id: `${t.id}-${h.statusKey}`,
              taskId: String(t.id),
              taskName: t.name,
              teamMemberId: t.teamMemberId,
              teamMemberName: t.teamMemberName,
              action: h.statusKey,
              timestamp: toMs(h.statusTimestamp),
              taskOrder: t.taskOrder ?? undefined,
            });
          });
        });

        if (projectInfo.isSequential) {
          tasksArray.sort((a, b) => (a.taskOrder || 0) - (b.taskOrder || 0));
        } else {
          tasksArray.sort((a, b) => (b.lastUpdatedAt || 0) - (a.lastUpdatedAt || 0));
        }
        activitiesArray.sort((a, b) => b.timestamp - a.timestamp);

        setProject(projectInfo);
        setTasks(tasksArray);
        setActivityItems(activitiesArray);
      } catch (error) {
        console.error('Proje verileri alınırken hata:', error);
        setError('Proje verileri alınırken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    loadProjectData();
  }, [open, projectSqlId, companyId]);

  // Tarih formatı
  const formatDate = (timestamp: number): string => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Sadece tarih formatı (saat olmadan)
  const formatDateOnly = (timestamp: number): string => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR');
  };
  
  // Aynı günde mi kontrol et
  const isSameDay = (timestamp1: number, timestamp2: number): boolean => {
    if (!timestamp1 || !timestamp2) return false;
    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };
  
  // Öncelik rengini belirle 
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#F44336'; // Kırmızı
      case 'medium':
        return '#FF9800'; // Turuncu
      case 'low':
        return '#4CAF50'; // Yeşil
      default:
        return '#9E9E9E'; // Gri
    }
  };
  
  // Öncelik metni
  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'Yüksek';
      case 'medium':
        return 'Orta';
      case 'low':
        return 'Düşük';
      default:
        return 'Belirtilmemiş';
    }
  };

  // Görev tipini getir
  const getTaskTypeLabel = (isSequential: boolean) => {
    return isSequential ? 'Sıralı Görev' : 'Normal Görev';
  };

  // Görev tipi rengini belirle
  const getTaskTypeColor = (isSequential: boolean) => {
    return isSequential ? '#673AB7' : '#009688'; // Mor: Sıralı, Turkuaz: Normal
  };
  
  // Aktivite işlemi için renk belirle
  const getActionColor = (action: string) => {
    switch (action) {
      case 'accepted':
        return '#E8F5E9'; // Açık yeşil
      case 'started':
        return '#FFF3E0'; // Açık turuncu
      case 'paused':
        return '#EFEBE9'; // Açık kahverengi
      case 'resumed':
        return '#FFF8E1'; // Açık sarı
      case 'completed':
        return '#E8F5E9'; // Açık yeşil
      default:
        return '#F5F5F5'; // Açık gri
    }
  };
  
  // Aktivite için ikon belirle
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'accepted':
        return <ThumbUpIcon fontSize="small" />;
      case 'started':
        return <PlayArrowIcon fontSize="small" />;
      case 'paused':
        return <PauseIcon fontSize="small" />;
      case 'resumed':
        return <ReplayIcon fontSize="small" />;
      case 'completed':
        return <DoneAllIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };
  
  // Durum kelimesine göre aksiyon adı al
  const getActionNameByStatus = (statusKey: string): string => {
    switch (statusKey) {
      case 'accepted':
        return 'Görev kabul edildi';
      case 'started':
        return 'Görev başlatıldı';
      case 'paused':
        return 'Görev duraklatıldı';
      case 'resumed':
        return 'Görev devam ettirildi';
      case 'completed':
        return 'Görev tamamlandı';
      default:
        return 'Bilinmeyen durum';
    }
  };
  
  // Durum simgesini ve rengini belirle
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Beklemede',
          icon: <PendingIcon />,
          color: '#9E9E9E'
        };
      case 'accepted':
        return {
          label: 'Kabul Edildi',
          icon: <CheckCircleIcon />,
          color: '#2196F3'
        };
      case 'in_progress':
        return {
          label: 'Devam Ediyor',
          icon: <PlayCircleFilledIcon />,
          color: '#FF9800'
        };
      case 'paused':
        return {
          label: 'Duraklatıldı',
          icon: <PauseIcon />,
          color: '#795548'
        };
      case 'completed':
        return {
          label: 'Tamamlandı',
          icon: <DoneAllIcon />,
          color: '#4CAF50'
        };
      default:
        return {
          label: 'Bilinmiyor',
          icon: <InfoIcon />,
          color: '#9E9E9E'
        };
    }
  };

  // Yükleniyor ekranı
  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Proje Detayları</Typography>
            <IconButton onClick={onClose} size="small" aria-label="Kapat">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Proje bilgileri yükleniyor...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Hata ekranı
  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Proje Detayları</Typography>
            <IconButton onClick={onClose} size="small" aria-label="Kapat">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Tamamlanan görev sayısı
  const completedTasksCount = tasks.filter(task => task.status === 'completed').length;
  
  // İlerleme yüzdesi
  const progressPercentage = tasks.length > 0 
    ? Math.round((completedTasksCount / tasks.length) * 100) 
    : 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        {project && (
          <>
            <DialogTitle sx={{ px: 3, pb: 1, pt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">{project.name}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small" edge="end" aria-label="Kapat">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <Divider />
            
            <StyledDialogContent>
              {/* Üst Bilgi Kartları */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Proje Bilgileri Kartı */}
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                      Proje Bilgileri
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <GroupsIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.5rem' }} />
                            <Typography variant="h6" fontWeight="medium">
                              {project.teamName}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, ml: 0.5 }}>
                            <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1" color="text.secondary">
                              Oluşturulma: {formatDate(project.createdAt)}
                            </Typography>
                          </Box>
                          
                          {(project.groupName || project.branchName) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {project.groupName && (
                                <Chip 
                                  label={`Grup: ${project.groupName}`} 
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                              
                              {project.branchName && (
                                <Chip 
                                  label={`Şube: ${project.branchName}`} 
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary.main">
                            Görev Tipi
                          </Typography>
                          <Chip 
                            label={getTaskTypeLabel(project.isSequential)} 
                            size="medium"
                            sx={{ 
                              bgcolor: `${getTaskTypeColor(project.isSequential)}20`,
                              color: getTaskTypeColor(project.isSequential),
                              fontWeight: 'bold',
                              fontSize: '0.95rem',
                              py: 2.5,
                              height: 'auto'
                            }}
                          />
                          
                          {project.isSequential && project.activeTaskOrder !== undefined && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                                <AssignmentIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                                Aktif Görev Sırası: <Box component="span" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>{project.activeTaskOrder}</Box>
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {project.description && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom color="primary.main" fontWeight="medium">
                          Proje Açıklaması:
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                          <Typography variant="body2">
                            {project.description}
                          </Typography>
                        </Paper>
                      </>
                    )}
                  </Paper>
                </Grid>
                
                {/* İlerleme Kartı */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                      Görev Durumu
                    </Typography>
                    
                    <Box sx={{ my: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Box sx={{ position: 'relative', display: 'inline-flex', width: '100%', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress 
                          variant="determinate" 
                          value={progressPercentage} 
                          size={120}
                          thickness={5}
                          sx={{ color: progressPercentage === 100 ? 'success.main' : 'primary.main' }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="h4"
                            component="div"
                            color={progressPercentage === 100 ? 'success.main' : 'primary.main'}
                            fontWeight="bold"
                          >
                            {progressPercentage}%
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                        {completedTasksCount} / {tasks.length} görev tamamlandı
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Chip 
                          icon={<PendingIcon />}
                          label={`${tasks.filter(t => t.status === 'pending').length} Beklemede`} 
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                        <Chip 
                          icon={<PlayCircleFilledIcon />}
                          label={`${tasks.filter(t => t.status === 'in_progress').length} Devam Ediyor`} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip 
                          icon={<DoneAllIcon />}
                          label={`${tasks.filter(t => t.status === 'completed').length} Tamamlandı`} 
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              
              {/* Sekmeler */}
              <Paper sx={{ mb: 3 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AssignmentIcon sx={{ mr: 1 }} />
                        <span>Görevler</span>
                        <Badge 
                          badgeContent={tasks.length} 
                          color="primary" 
                          sx={{ ml: 2.5 }}
                        />
                      </Box>
                    } 
                  />
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimelineIcon sx={{ mr: 1 }} />
                        <span>Akış</span>
                        <Badge 
                          badgeContent={activityItems.length} 
                          color="primary" 
                          sx={{ ml: 2.5 }}
                        />
                      </Box>
                    } 
                  />
                </Tabs>
              </Paper>
              
              {/* Görevler Tab Paneli */}
              <TabPanel value={tabValue} index={0}>
                <TableContainer component={Paper} sx={{ maxHeight: 350, overflow: 'auto' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {project.isSequential && (
                          <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Sıra</TableCell>
                        )}
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Görev Adı</TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Sorumlu</TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Durum</TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Öncelik</TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Oluşturulma</TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Son Güncelleme</TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Açıklama</TableCell>
                        <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>Resimler</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={project.isSequential ? 9 : 8} align="center">
                            <Typography variant="body2" color="text.secondary">
                              Bu projede henüz görev tanımlanmamış
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        tasks
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((task) => {
                            const statusInfo = getStatusInfo(task.status);
                            const isActiveTask = project.isSequential && project.activeTaskOrder === task.taskOrder;
                            
                            return (
                              <TableRow 
                                key={task.id} 
                                hover
                                sx={isActiveTask ? 
                                  { bgcolor: 'primary.50', '& > td': { py: 1 } } : 
                                  { '& > td': { py: 1 } }
                                }
                              >
                                {project.isSequential && (
                                  <TableCell>
                                    <Chip 
                                      label={`#${task.taskOrder}`} 
                                      size="small"
                                      color={isActiveTask ? "primary" : "default"}
                                      variant={isActiveTask ? "filled" : "outlined"}
                                    />
                                  </TableCell>
                                )}
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {task.name}
                                  </Typography>
                                  {task.description && (
                                    <Tooltip title={task.description}>
                                      <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
                                        <InfoIcon fontSize="small" color="action" />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TaskMemberAvatar>
                                      {task.teamMemberName ? task.teamMemberName.charAt(0) : '?'}
                                    </TaskMemberAvatar>
                                    <Typography variant="body2">
                                      {task.teamMemberName}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <StatusChip
                                    icon={statusInfo.icon}
                                    label={statusInfo.label}
                                    size="small"
                                    customcolor={statusInfo.color}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    icon={<FlagIcon />}
                                    label={getPriorityLabel(task.priority)} 
                                    size="small"
                                    sx={{ 
                                      bgcolor: `${getPriorityColor(task.priority)}20`,
                                      color: getPriorityColor(task.priority),
                                      fontWeight: 'medium'
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {formatDate(task.createdAt)}
                                </TableCell>
                                <TableCell>
                                  {task.lastUpdatedAt ? (
                                    <>
                                      {formatDate(task.lastUpdatedAt)}
                                      {task.lastUpdatedByName && (
                                        <Typography variant="caption" display="block" color="text.secondary">
                                          {task.lastUpdatedByName}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {task.description ? (
                                    <Tooltip title={task.description}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          fontSize: '0.75rem',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          maxWidth: '200px',
                                          minHeight: '32px',
                                          lineHeight: '16px'
                                        }}
                                      >
                                        {task.description}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {task.imageUrls && task.imageUrls.length > 0 ? (
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      {task.imageUrls.map((url: string, imgIndex: number) => (
                                        <Tooltip key={imgIndex} title="Resmi görüntülemek için tıklayın">
                                          <Box
                                            component="img"
                                            src={url}
                                            alt={`Görev Resmi ${imgIndex + 1}`}
                                            sx={{
                                              width: 36,
                                              height: 36,
                                              borderRadius: 1,
                                              border: '1px solid #eee',
                                              cursor: 'pointer'
                                            }}
                                            onClick={() => handleOpenImageDialog(url)}
                                          />
                                        </Tooltip>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        -
                                      </Typography>
                                    </Box>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {tasks.length > 0 && (
                  <TablePagination
                    component="div"
                    count={tasks.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Sayfa başına görev:"
                    labelDisplayedRows={({ from, to, count }) => `${count} görevden ${from}-${to} arası`}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{ borderTop: '1px solid #e0e0e0' }}
                  />
                )}
              </TabPanel>
              
              {/* Akış Tab Paneli */}
              <TabPanel value={tabValue} index={1}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom 
                    sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 1 }} />
                    Görev Durum Değişiklikleri
                  </Typography>
                  
                  {activityItems.length === 0 ? (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Bu projede henüz bir görev durum değişikliği kaydedilmemiş.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TableContainer sx={{ maxHeight: 350, mt: 2 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '20%' }}>Tarih/Saat</TableCell>
                              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '25%' }}>Görev</TableCell>
                              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '25%' }}>Personel</TableCell>
                              <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '30%' }}>Durum Değişikliği</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {activityItems
                              .slice(activityPage * activityRowsPerPage, activityPage * activityRowsPerPage + activityRowsPerPage)
                              .map((activity, index, filteredActivities) => {
                                // Tarih ayracı eklemek için önceki aktivite ile karşılaştır
                                const prevActivity = index > 0 ? filteredActivities[index - 1] : null;
                                const showDateDivider = !prevActivity || !isSameDay(activity.timestamp, prevActivity.timestamp);
                                
                                return (
                                  <React.Fragment key={activity.id}>
                                    {showDateDivider && (
                                      <TableRow>
                                        <TableCell 
                                          colSpan={4} 
                                          sx={{ 
                                            bgcolor: 'primary.50', 
                                            py: 1, 
                                            borderBottom: '1px solid rgba(224, 224, 224, 1)'
                                          }}
                                        >
                                          <Typography 
                                            variant="subtitle2" 
                                            fontWeight="bold"
                                            sx={{ display: 'flex', alignItems: 'center' }}
                                          >
                                            <CalendarTodayIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                            {formatDateOnly(activity.timestamp)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                    <TableRow hover sx={{ '& > td': { py: 0.8 } }}>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                          {new Date(activity.timestamp).toLocaleTimeString('tr-TR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit'
                                          })}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <AssignmentIcon 
                                            sx={{ mr: 1, fontSize: '1.25rem', color: 'text.secondary' }}
                                          />
                                          <Tooltip title={`Görev ID: ${activity.taskId}`}>
                                            <Typography variant="body2" fontWeight="medium">
                                              {activity.taskName}
                                              {project.isSequential && activity.taskOrder && (
                                                <Chip
                                                  label={`#${activity.taskOrder}`}
                                                  size="small"
                                                  color="primary"
                                                  variant="outlined"
                                                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                />
                                              )}
                                            </Typography>
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <TaskMemberAvatar>
                                            {activity.teamMemberName ? activity.teamMemberName.charAt(0) : '?'}
                                          </TaskMemberAvatar>
                                          <Typography variant="body2">
                                            {activity.teamMemberName || 'Belirtilmemiş'}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          icon={getActionIcon(activity.action)}
                                          label={getActionNameByStatus(activity.action)}
                                          size="small"
                                          variant="outlined"
                                          sx={{ 
                                            fontWeight: 'medium',
                                            bgcolor: getActionColor(activity.action)
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}
                              
                            {activityItems.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <HistoryIcon sx={{ fontSize: '2rem', color: 'text.secondary', opacity: 0.5 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      Henüz hiçbir durum değişikliği kaydedilmemiş
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={activityItems.length}
                        page={activityPage}
                        onPageChange={handleActivityChangePage}
                        rowsPerPage={activityRowsPerPage}
                        onRowsPerPageChange={handleActivityChangeRowsPerPage}
                        labelRowsPerPage="Sayfa başına kayıt:"
                        labelDisplayedRows={({ from, to, count }) => `${count} kayıttan ${from}-${to} arası`}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={{ borderTop: '1px solid #e0e0e0' }}
                      />
                    </>
                  )}
                </Paper>
              </TabPanel>
            </StyledDialogContent>
            
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button 
                onClick={onClose} 
                color="primary" 
                variant="outlined"
              >
                Kapat
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Resim Görüntüleme Diyaloğu */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Görev Resmi</Typography>
          <IconButton onClick={handleCloseImageDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Görev Resmi"
              sx={{
                maxWidth: '100%',
                maxHeight: 'calc(80vh - 64px)',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeamTaskDetailModal; 